/**
 * @fileoverview Shared mock user directory.
 * @description The one place every identity mock (login, verify-otp, accept-invitation) reads
 * "who is this user" from, so a freshly-accepted invitation is recognized consistently by all of
 * them — the same guarantee a real backend gets for free by having one users table. Combines the
 * fixed fixture list in mock-users.json with users registered at runtime (an accepted invitation),
 * kept in memory only: a page refresh loses a demo session's freshly-registered mock users, same
 * as every other in-memory mock store in this folder. Never imported outside this feature's
 * mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import mockUsers from './mock-users.json'

import type { BankingScope } from '@/features/auth/services/session.service'

export interface MockUserRecord {
  id: string
  email: string
  /** The account holder's display name — optional since an account registered straight from an
   * invitation (see invitation-mock-store.ts) never had one captured. */
  name?: string
  password: string
  /** Picks the permission codes capabilities-mock.service.ts grants this user. */
  activeRoleKey: string
  mustChangePassword: boolean
  bankingScope?: BankingScope
  /** REGISTERED once an invitation is accepted, until the activation code is verified; absent
   * means ACTIVE (every fixture user in mock-users.json). */
  accountStatus?: 'REGISTERED' | 'ACTIVE'
}

const staticUsers = mockUsers as MockUserRecord[]
const dynamicUsersById = new Map<string, MockUserRecord>()

/**
 * @description Adds (or replaces) a user this directory can subsequently find by email or id —
 * used once an invitation is accepted, since that account didn't exist in mock-users.json.
 * @param {MockUserRecord} user - The user to register.
 */
export function registerMockUser(user: MockUserRecord): void {
  dynamicUsersById.set(user.id, user)
}

/**
 * @description Finds a user by email, case-insensitively, across both the static fixture list and
 * any registered at runtime.
 * @param {string} email - The email address to look up.
 * @returns {MockUserRecord | undefined} The matching user, or undefined.
 */
export function findMockUserByEmail(email: string): MockUserRecord | undefined {
  const normalized = email.trim().toLowerCase()

  return (
    staticUsers.find((user) => user.email.toLowerCase() === normalized) ??
    [...dynamicUsersById.values()].find((user) => user.email.toLowerCase() === normalized)
  )
}

/**
 * @description Finds a user by id, across both the static fixture list and any registered at
 * runtime.
 * @param {string} userId - The user id to look up.
 * @returns {MockUserRecord | undefined} The matching user, or undefined.
 */
export function findMockUserById(userId: string): MockUserRecord | undefined {
  return staticUsers.find((user) => user.id === userId) ?? dynamicUsersById.get(userId)
}

/**
 * @description Marks a registered user ACTIVE, once their activation code is verified.
 * @param {string} userId - The user to activate.
 */
export function activateMockUser(userId: string): void {
  const user = findMockUserById(userId)

  if (user) {
    user.accountStatus = 'ACTIVE'
  }
}
